import clsx from 'clsx';
import { useCallback, useEffect, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { type Editor } from '@tiptap/react';
import { getAiToolkit } from '@tiptap-pro/ai-toolkit';
import { AnimatePresence } from 'motion/react';

import { useSuggestionHandlers } from '@/hooks/use-suggestion-handlers';
import type { ReviewAdapter } from '@/hooks/llm/use-llm-agent';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Button } from '@flintmint/web-shared/components/button';

import styles from './suggestion-navigation.module.scss';

const EMPTY_SUGGESTIONS: ReviewAdapter['suggestions'] = [];
const noop = () => {};

export const SuggestionsNavigation = (props: SuggestionsNavigationProps) => {
  const { editor, onAllResolved, review, className, ...rest } = props;

  const currentIndex = review?.currentIndex ?? 0;
  const suggestions = useMemo(() => review?.suggestions ?? EMPTY_SUGGESTIONS, [review]);

  const totalSuggestions = suggestions.length;

  useEffect(() => {
    if (suggestions.length > 0) {
      document.body.classList.add('ai-toolkit-review-mode');
    } else {
      document.body.classList.remove('ai-toolkit-review-mode');
    }
  }, [suggestions.length]);

  useEffect(() => {
    return () => {
      document.body.classList.remove('ai-toolkit-review-mode');
    };
  }, []);

  const handleAllResolved = useCallback(
    (contentChanged: boolean, batchAcceptedCount: number) => {
      void review?.handleAllResolved(contentChanged, batchAcceptedCount, onAllResolved);
    },
    [onAllResolved, review]
  );

  const setSuggestions = review?.setSuggestions ?? noop;
  const setCurrentIndex = review?.setCurrentIndex ?? noop;
  const getSuggestions = review?.getCurrentSuggestions;
  const acceptedCountBase = review?.acceptedCountBase ?? 0;
  const ensureLiveSuggestions = review?.ensureLiveSuggestions;

  const { handleAccept, handleReject, handleNext, handlePrevious, handleRejectAll, handleAcceptAll, navigateToSuggestion } =
    useSuggestionHandlers(
      editor,
      suggestions,
      currentIndex,
      setSuggestions,
      setCurrentIndex,
      handleAllResolved,
      getSuggestions,
      acceptedCountBase,
      ensureLiveSuggestions
    );

  useEffect(() => {
    if (!editor || !suggestions.length) return;

    const frameId = requestAnimationFrame(() => {
      const selectedSuggestion = getAiToolkit(editor).getSelectedSuggestion?.();
      if (selectedSuggestion?.id) return;

      navigateToSuggestion(currentIndex);
    });

    return () => cancelAnimationFrame(frameId);
  }, [currentIndex, editor, navigateToSuggestion, suggestions.length]);

  return (
    <div className={clsx(styles.absoluteHolder, className)} {...rest}>
      <div className={styles.contentBox}>
        <AnimatePresence key="suggestions-navigation">
          {!!suggestions.length && (
            <div className={styles.suggestionNavigation}>
              {totalSuggestions > 1 && (
                <>
                  <div className={styles.buttonGroup}>
                    <Button
                      size="medium"
                      cornerRadius="smooth"
                      onClick={handleRejectAll}
                      appearance="clear"
                      data-testid="reject-all-diffs-button"
                    >
                      Reject all
                    </Button>
                    <Button size="medium" cornerRadius="smooth" data-style="ghost" onClick={handleAcceptAll} appearance="clear">
                      Accept all
                    </Button>
                  </div>
                  <div className={styles.navigationIndexHolder}>
                    <button
                      className={clsx(styles.nextSuggestion, {
                        [styles.disabled]: !currentIndex,
                      })}
                      onClick={handlePrevious}
                    >
                      <Icon size={12} type="arrow-left-02" />
                    </button>
                    <div className={styles.suggestionsIndex}>
                      <span className={styles.currentIndex}>{currentIndex + 1}</span>
                      <span>/</span>
                      <span>{totalSuggestions}</span>
                    </div>
                    <button
                      className={clsx(styles.nextSuggestion, {
                        [styles.disabled]: currentIndex + 1 >= totalSuggestions,
                      })}
                      onClick={handleNext}
                    >
                      <Icon size={14} type="arrow-right-02" />
                    </button>
                  </div>
                </>
              )}
              <div className={styles.buttonGroup}>
                <Button
                  size="medium"
                  cornerRadius="smooth"
                  onClick={handleReject}
                  appearance="plain"
                  color="danger"
                  icon={<Icon size={18} type="multiplication-sign" />}
                  data-testid="reject-diff-button"
                >
                  Reject
                </Button>
                <Button
                  size="medium"
                  color="success"
                  cornerRadius="smooth"
                  onClick={handleAccept}
                  icon={<Icon size={18} type="tick-02" />}
                  data-testid="accept-diff-button"
                >
                  Accept
                </Button>
              </div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

interface SuggestionsNavigationProps {
  editor: Editor;
  className?: string;
  style?: CSSProperties;
  review: ReviewAdapter;
  onAllResolved?: (contentChanged: boolean) => void;
}
